// US-equity market session calendar — single source of truth for
// any web-side code that needs ET time, NYSE session classification,
// or DST-correct time math. Mirrors iOS Design/MarketCalendar.swift.
//
// DST handling: openTimeET() uses Intl.DateTimeFormat round-trip to
// derive the actual America/New_York offset for any given date, so
// winter (EST -05:00) and summer (EDT -04:00) both produce correct
// 09:30 ET timestamps. Holiday set is the NYSE 2026 observed schedule.

import { useEffect, useState } from 'react'

export type MarketSession = 'regular' | 'preMarket' | 'afterHours' | 'overnight' | 'weekend' | 'holiday'

export type MarketStatus = {
  session: MarketSession
  nextOpen: Date | null // null when session === 'regular'
}

type MarketLanguage = 'en' | 'zh'

// NYSE 2026 holidays in ET (YYYY-MM-DD)
export const NYSE_HOLIDAYS_2026 = new Set<string>([
  '2026-01-01', '2026-01-19', '2026-02-16', '2026-04-03',
  '2026-05-25', '2026-06-19', '2026-07-03', '2026-09-07',
  '2026-11-26', '2026-12-25',
])

export function nowInET(at: Date = new Date()): { mins: number; weekday: number; ymd: string } {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', weekday: 'short',
    hour12: false,
  })
  const parts: Record<string, string> = {}
  for (const p of fmt.formatToParts(at)) parts[p.type] = p.value
  const hour = parseInt(parts.hour, 10)
  const minute = parseInt(parts.minute, 10)
  const mins = hour * 60 + minute
  const weekdayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
  const weekday = weekdayMap[parts.weekday] ?? 0
  const ymd = `${parts.year}-${parts.month}-${parts.day}`
  return { mins, weekday, ymd }
}

export function openTimeET(ymd: string): Date {
  const candidate = new Date(`${ymd}T09:30:00Z`)
  const etParts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(candidate)
  const etHour = parseInt(etParts.find((p) => p.type === 'hour')!.value, 10)
  const etMin = parseInt(etParts.find((p) => p.type === 'minute')!.value, 10)
  const etMinsAtCandidate = etHour * 60 + etMin
  const targetMins = 9 * 60 + 30
  const offsetMs = (targetMins - etMinsAtCandidate) * 60 * 1000
  return new Date(candidate.getTime() + offsetMs)
}

export function addDaysET(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + days)
  const ny = dt.getUTCFullYear()
  const nm = String(dt.getUTCMonth() + 1).padStart(2, '0')
  const nd = String(dt.getUTCDate()).padStart(2, '0')
  return `${ny}-${nm}-${nd}`
}

export function nextRegularOpen(fromYmd: string): Date {
  let cursor = fromYmd
  for (let i = 0; i < 14; i++) {
    const probe = new Date(`${cursor}T12:00:00Z`)
    const wkParts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', weekday: 'short' }).format(probe)
    const wd = ({ Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 } as Record<string, number>)[wkParts] ?? 0
    if (wd !== 0 && wd !== 6 && !NYSE_HOLIDAYS_2026.has(cursor)) {
      return openTimeET(cursor)
    }
    cursor = addDaysET(cursor, 1)
  }
  return openTimeET(fromYmd)
}

export function getMarketStatus(at: Date = new Date()): MarketStatus {
  const { mins, weekday, ymd } = nowInET(at)
  const isWeekend = weekday === 0 || weekday === 6
  const isHoliday = NYSE_HOLIDAYS_2026.has(ymd)

  if (isWeekend) return { session: 'weekend', nextOpen: nextRegularOpen(ymd) }
  if (isHoliday) return { session: 'holiday', nextOpen: nextRegularOpen(ymd) }

  if (mins >= 570 && mins < 960)  return { session: 'regular',    nextOpen: null }
  if (mins >= 240 && mins < 570)  return { session: 'preMarket',  nextOpen: openTimeET(ymd) }
  if (mins >= 960 && mins < 1200) return { session: 'afterHours', nextOpen: nextRegularOpen(addDaysET(ymd, 1)) }
  if (mins < 240)                 return { session: 'overnight',  nextOpen: openTimeET(ymd) }
  return { session: 'overnight', nextOpen: nextRegularOpen(addDaysET(ymd, 1)) }
}

export const SESSION_LABEL: Record<MarketSession, string> = {
  regular:    'Open',
  preMarket:  'Pre-Market',
  afterHours: 'After-Hours',
  overnight:  'Closed',
  weekend:    'Closed',
  holiday:    'Closed',
}

export const SESSION_TONE: Record<MarketSession, 'open' | 'soft' | 'closed'> = {
  regular: 'open',
  preMarket: 'soft', afterHours: 'soft',
  overnight: 'closed', weekend: 'closed', holiday: 'closed',
}

export function formatTimeToOpen(now: Date, next: Date, language: MarketLanguage = 'en'): string {
  const interval = (next.getTime() - now.getTime()) / 1000
  if (interval <= 0) return language === 'zh' ? '即将开盘' : 'Opens soon'

  const nowET = nowInET(now)
  const nextETParts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: 'short', day: '2-digit',
    weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(next)
  const p: Record<string, string> = {}
  for (const part of nextETParts) p[part.type] = part.value
  // Reconstruct YMD for diff comparison
  const ymdFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(next)
  const yp: Record<string, string> = {}
  for (const part of ymdFmt) yp[part.type] = part.value
  const nextYmd = `${yp.year}-${yp.month}-${yp.day}`

  let dayDiff = 0
  {
    let cursor = nowET.ymd
    while (cursor < nextYmd && dayDiff < 30) { cursor = addDaysET(cursor, 1); dayDiff++ }
  }

  if (dayDiff === 0) {
    const total = Math.floor(interval / 60)
    const h = Math.floor(total / 60)
    const m = total % 60
    if (language === 'zh') {
      if (h === 0) return `${m} 分钟后开盘`
      return `${h} 小时 ${m} 分钟后开盘`
    }
    if (h === 0) return `Opens in ${m}m`
    return `Opens in ${h}h ${m}m`
  }

  if (dayDiff <= 7) {
    const hh = parseInt(p.hour, 10)
    if (language === 'zh') {
      const date = new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'America/New_York',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(next)
      return `${date} 开盘`
    }
    return `Opens ${p.weekday} ${hh}:${p.minute}`
  }
  const hh2 = parseInt(p.hour, 10)
  if (language === 'zh') {
    const date = new Intl.DateTimeFormat('zh-CN', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(next)
    return `${date} 开盘`
  }
  return `Opens ${p.month} ${parseInt(p.day, 10)} ${hh2}:${p.minute}`
}

export function useMarketStatus(): MarketStatus {
  const [status, setStatus] = useState<MarketStatus>(() => getMarketStatus())
  useEffect(() => {
    const tick = () => setStatus(getMarketStatus())
    const id = window.setInterval(tick, 15 * 1000)
    return () => window.clearInterval(id)
  }, [])
  return status
}
